/* eslint-disable @next/next/no-img-element */
import {
  Banner as OfferBannerComp,
  BannerSlider,
  BannerSliderImage,
  BannerSliderSlideItem,
  Image,
  UnstyledLink,
  useSliderContext
} from '@cash-rewards/cr-ui-components';
import {
  ARRAY_ELEMENT_ID,
  ELEMENT_POSITION,
  EVENT_NAME,
  EventAttributes
} from '@cash-rewards/mparticlejs';
import { useGetFlag } from '@cash-rewards/optimizely';
import {
  extractUtmQuery,
  getOfferUrl,
  isOfferBanner
} from '@cash-rewards/reactjs';
import {
  bannerProductMapper,
  offerBannerClickEventMapper,
  offerBannerProductMapper,
  ProductMapperConfig,
  useCarouselClickTracking,
  useCarouselImpressionTracking
} from '@cash-rewards/reactjs/client';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';

import '@cash-rewards/cr-ui-components/slider.css';

import useIsDesktopSize from '@/hooks/useIsDesktopSize';
import useIsTabletSize from '@/hooks/useIsTabletSize';

import { featureToggle } from '@/constants/FeatureToggles';
import { ON } from '@/constants/variations';
import { OFFER_BADGE_MAP } from '@/modules/core/constants/badgeType';
import { brazeToBannerMapper } from '@/modules/core/mappers/brazeMapper';
import { filterByFeedId } from '@/modules/core/utils/braze';
import ImpressionPixel from '@/modules/shop/components/ImpressionPixel';
import useBraze from '@/modules/shop/hooks/useBraze';
import { BrazeSdk } from '@/modules/shop/models/braze';
import {
  Banner,
  OfferBanner,
  OfferBannerAd,
  OfferModel,
  ShopBanner
} from '@/modules/shop/models/offerModels';
interface ShopBannersProps {
  banners: ShopBanner[];
}

const BannerImpression = ({
  index,
  banner,
  brazeSdk
}: {
  index: number;
  banner: Banner;
  brazeSdk?: BrazeSdk;
}) => {
  const { activeIndex } = useSliderContext();
  // useEffect responsible for tracking braze content cards impressions
  useEffect(() => {
    if (!banner.brazeContentCardInstance) {
      return;
    }
    if (index !== activeIndex) {
      return;
    }
    try {
      brazeSdk?.logContentCardImpressions([banner.brazeContentCardInstance]);
      // eslint-disable-next-line no-empty
    } catch (brazeUninitialized) {}
  }, [activeIndex, index, banner, brazeSdk]);

  return null;
};

const ShopBanners = ({ banners }: ShopBannersProps) => {
  const router = useRouter();
  const isDesktopSize = useIsDesktopSize();
  const isTabletSize = useIsTabletSize();
  const offersCashBackCapFlag = useGetFlag(featureToggle.OffersCashbackCap);
  const useCashbackCap =
    offersCashBackCapFlag?.enabled && offersCashBackCapFlag.variationKey === ON;

  const isMobile = !isTabletSize && !isDesktopSize;
  const firstFeedId = 'shop-page-banner';
  const fourthFeedId = 'shop-page-banner-position-four';
  const fifthFeedId = 'shop-page-banner-position-five';

  const brazeFeedId = `${firstFeedId},${fourthFeedId},${fifthFeedId}`;

  const { contentCards, brazeSdk } = useBraze(brazeFeedId);

  const allBanners = useMemo(() => {
    const brazeContentCards = (contentCards || []).map(brazeToBannerMapper);
    const firstPositionedBanners = brazeContentCards.filter((banner) =>
      (banner.brazeContentCardInstance?.extras.feed_id ?? '')
        .split(',')
        .includes(firstFeedId)
    );
    const allBanners = [...firstPositionedBanners, ...banners];

    const fourthPositionedBanners: ShopBanner[] = brazeContentCards.filter(
      (banner) =>
        (banner.brazeContentCardInstance?.extras.feed_id ?? '')
          .split(',')
          .includes(fourthFeedId)
    );
    allBanners.splice(3, 0, ...fourthPositionedBanners);
    const fifthPositionedBanners: ShopBanner[] = filterByFeedId(
      brazeContentCards,
      fifthFeedId
    );
    allBanners.splice(4, 0, ...fifthPositionedBanners);
    return allBanners;
  }, [banners, contentCards]);

  const attributeOverrides: EventAttributes = {
    ...extractUtmQuery(router.query),
    element_position: ELEMENT_POSITION.top
  };

  const { logEvent } = useCarouselClickTracking(
    ARRAY_ELEMENT_ID.banner,
    [bannerProductMapper],
    [],
    attributeOverrides,
    EVENT_NAME.click_web_banner,
    { device: isMobile ? 'mobile' : 'desktop' }
  );

  const { logEvent: logEventOfferBanner } = useCarouselClickTracking(
    ARRAY_ELEMENT_ID.banner,
    [offerBannerProductMapper],
    [offerBannerClickEventMapper],
    attributeOverrides,
    EVENT_NAME.click_web_banner,
    { device: isMobile ? 'mobile' : 'desktop' }
  );

  const unifiedBannerProductMapper = (
    index: number,
    banner: Banner | OfferBanner,
    config: ProductMapperConfig
  ) => {
    if (isOfferBanner(banner)) {
      return offerBannerProductMapper(index, banner, config);
    } else {
      return bannerProductMapper(index, banner, config);
    }
  };

  const { onRendered } = useCarouselImpressionTracking(
    ARRAY_ELEMENT_ID.banner,
    allBanners as (Banner | OfferBanner)[],
    [unifiedBannerProductMapper],
    attributeOverrides,
    EVENT_NAME.impression_web_banner_render,
    { device: isMobile ? 'mobile' : 'desktop' }
  );

  const totalSlides = allBanners.length;

  const handleMounted = useCallback(() => {
    if (!allBanners.length) return;

    onRendered(allBanners);
  }, [allBanners, onRendered]);

  const getBannerMerchantName = (banner: ShopBanner) => {
    if (isOfferBanner(banner)) {
      return banner.merchant.name;
    }
    // OfferBannerAd name
    return banner.name;
  };

  const getBannerImage = (banner: ShopBanner) => {
    if (isOfferBanner(banner)) {
      return banner.offerBackgroundImageUrl;
    }

    return isMobile ? banner.mobileBrowserImageUrl : banner.desktopImageUrl;
  };

  const getBannerLink = (banner: ShopBanner) => {
    if (isOfferBanner(banner)) {
      return getOfferUrl(banner);
    } else {
      return isMobile ? banner.mobileBrowserLink : banner.desktopLink;
    }
  };

  return (
    <div>
      {allBanners.map((banner) => {
        return (
          !isOfferBanner(banner) && (
            <ImpressionPixel
              key={banner.id}
              impressionUrl={banner.impressionUrl}
            />
          )
        );
      })}
      <BannerSlider
        key={totalSlides}
        totalSlides={allBanners.length}
        autoPlay={true}
        rewind={true}
        onMounted={handleMounted}
      >
        {allBanners.map((banner, idx) => {
          const key = banner.id;
          const name = getBannerMerchantName(banner);
          const bannerImage = getBannerImage(banner);
          const link = getBannerLink(banner);

          return (
            <BannerSliderSlideItem key={key} index={idx}>
              <UnstyledLink
                href={link}
                onClick={() => {
                  const currentPage = idx + 1;
                  if (isOfferBanner(banner)) {
                    logEventOfferBanner(idx, banner, currentPage, 1);
                  } else {
                    logEvent(idx, banner, currentPage, 1);
                  }
                  if (
                    !isOfferBanner(banner) &&
                    banner.brazeContentCardInstance
                  ) {
                    brazeSdk?.logContentCardClick(
                      banner.brazeContentCardInstance
                    );
                  }
                }}
                className='w-full'
                data-test-id={`banner-${idx}`}
              >
                {!isOfferBanner(banner) && banner.brazeContentCardInstance ? (
                  <Image // Braze content cards tracking requires the image to be fetched by the client, it cant use next image optimization
                    alt='Campaign banner'
                    src={banner.desktopImageUrl}
                    className='w-full rounded-cr-lg object-cover'
                  />
                ) : (
                  <>
                    {/* support legacy image only AD banner */}
                    {!isOfferBanner(banner) && !isDynamicAdBanner(banner) && (
                      <BannerSliderImage
                        as='img'
                        alt={`${name} banner`}
                        src={bannerImage}
                        loading='lazy'
                      />
                    )}
                    {isDynamicAdBanner(banner) && (
                      <DynamicBanner
                        banner={banner}
                        bannerImage={bannerImage}
                      />
                    )}
                    {isOfferBanner(banner) && (
                      <DynamicBanner
                        banner={banner}
                        useCashbackCap={useCashbackCap}
                      ></DynamicBanner>
                    )}
                  </>
                )}
                {!isOfferBanner(banner) && (
                  <BannerImpression
                    banner={banner}
                    index={idx}
                    brazeSdk={brazeSdk}
                  />
                )}
              </UnstyledLink>
            </BannerSliderSlideItem>
          );
        })}
      </BannerSlider>
    </div>
  );
};

interface OfferBannerProps {
  banner: OfferModel | OfferBannerAd;
  useCashbackCap?: boolean;
  flashSalesBadgeOverride?: string;
  bannerImage?: string;
}

const DynamicBanner = ({
  banner,
  useCashbackCap,
  bannerImage
}: OfferBannerProps) => {
  const terms = banner.bannerTCs;

  let offerBadge;
  if (((banner as OfferModel).flashSalesBadgeOverride ?? '').trim()) {
    offerBadge = OFFER_BADGE_MAP['FlashSale'];
  } else if ((banner as OfferModel).offerBadge) {
    offerBadge = OFFER_BADGE_MAP[(banner as OfferModel).offerBadge];
  }

  const cashbackCap = useMemo(() => {
    if (useCashbackCap) {
      return (banner as OfferModel).cashbackCapDescription;
    }
    const cappedIndex = ((banner as OfferModel).wasRate || '')
      .trim()
      .toLowerCase()
      .indexOf('capped');
    const strippedWasRate =
      cappedIndex > -1
        ? (banner as OfferModel).wasRate.slice(cappedIndex)
        : undefined;

    return strippedWasRate;
  }, [banner, useCashbackCap]);

  const dynamicAdBanner = isDynamicAdBanner(banner);

  if (dynamicAdBanner) {
    const {
      bannerTitle,
      termsAndConditions,
      merchantLogoOverride,
      merchantLogoUrl,
      highlightCopyInPurpleBold,
      subCopy,
      highlightCopyInPurpleLight,
      merchantName,
      merchantCommissionString,
      singleTierEndDate,
      cashbackCap,
      colorVariant
    } = banner || {};
    return (
      <OfferBannerComp
        bannerTitle={bannerTitle || (merchantName ?? '')}
        terms={termsAndConditions || 'Limited time. T&Cs apply.'}
        logoUrl={merchantLogoOverride || merchantLogoUrl}
        imageSrc={bannerImage}
        commissionString={
          highlightCopyInPurpleBold || (merchantCommissionString ?? '')
        }
        singleTierEndDate={singleTierEndDate}
        couponCode={subCopy}
        hideCodeLabel={true}
        wasRate={highlightCopyInPurpleLight || cashbackCap}
        colorVariant={colorVariant}
      ></OfferBannerComp>
    );
  }
  const {
    title,
    clientCommissionString,
    endDateTimeOffset,
    flashSalesBadgeOverride,
    merchantLogoUrl,
    offerBackgroundImageUrl,
    merchant,
    colorVariant
  } = banner;

  return (
    <OfferBannerComp
      bannerTitle={title}
      commissionString={clientCommissionString}
      endDate={endDateTimeOffset}
      terms={terms === null ? undefined : terms}
      wasRate={cashbackCap}
      badgeType={offerBadge}
      flashSalesBadgeOverride={flashSalesBadgeOverride}
      logoUrl={merchantLogoUrl}
      imageSrc={offerBackgroundImageUrl}
      isGiftCard={merchant?.isEonxGiftCard}
      colorVariant={colorVariant}
    ></OfferBannerComp>
  );
};

export default ShopBanners;

const isDynamicAdBanner = (
  banner: OfferModel | Banner | OfferBannerAd
): banner is OfferBannerAd => {
  return (banner as OfferBannerAd).isDynamic === true;
};
